import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Input,
  Label,
  SearchableSelect,
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useModal,
} from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import {
  useCreateDriverOnShiftMutation,
  useUpdateDriverOnShiftMutation,
  useCompleteDriverOnShiftMutation,
} from "~/shared/api/shift/driver-on-shift.grpc";
import {
  useChangeRouteDriverOnShiftMutation,
  useLazyListRoutesQuery,
} from "~/shared/api/routes/route.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import type { ShiftId } from "../hooks/useShiftApproval";

/** A shift lasts 12 hours — `tw_end_at` is derived from `tw_start_at` on edit */
const SHIFT_DURATION_HOURS = 12;

/** "00:00" … "23:00" — the start time is granular to whole hours, 24-hour format */
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, "0")}:00`);

/** Local hour of `date` as one of `HOUR_OPTIONS` */
const toHourOption = (date: Date): string => `${String(date.getHours()).padStart(2, "0")}:00`;

interface CreateShiftModalProps {
  drivers: { id: string; name: string }[];
  trucks: ConnectProto.Truck[];
  activeShifts?: { driver_id: number; truck_id: number }[];
  /** In create mode, receives the new shift. */
  onShiftCreated?: (created?: ConnectProto.DriverOnShift) => void;
  truckId?: string;
  activeDriverId?: string;
  activeShiftId?: ShiftId;
  activeShiftStatus?: ConnectProto.DriverOnShiftStatus;
  planStart?: any;
  defaultDate?: Date;
  trigger?: React.ReactNode;
}

export const CreateShiftModal: React.FC<CreateShiftModalProps> = ({
  drivers,
  trucks,
  activeShifts = [],
  onShiftCreated,
  truckId,
  activeDriverId,
  activeShiftId,
  activeShiftStatus,
  planStart,
  defaultDate,
  trigger,
}) => {
  const { openModal, closeModal } = useModal();
  const t = useT();

  const handleOpen = () => {
    const modalId = openModal({
      size: "md",
      title: activeShiftId
        ? t(Namespace.Autopark, "modals.changeDriver.title")
        : t(Namespace.Autopark, "modals.createShift.title"),
      bodyClassName: "!p-0 flex flex-col flex-1 min-h-0",
      closeButtonTop: "top-4",
      body: (
        <CreateShiftModalForm
          drivers={drivers}
          trucks={trucks}
          activeShifts={activeShifts}
          onShiftCreated={onShiftCreated}
          truckId={truckId}
          activeDriverId={activeDriverId}
          activeShiftId={activeShiftId}
          activeShiftStatus={activeShiftStatus}
          planStart={planStart}
          defaultDate={defaultDate}
          onClose={() => closeModal(modalId)}
        />
      ),
    });
  };

  if (trigger) {
    return React.cloneElement(trigger as React.ReactElement, {
      onClick: (e: React.MouseEvent) => {
        if ((trigger as React.ReactElement).props.onClick) {
          (trigger as React.ReactElement).props.onClick(e);
        }
        handleOpen();
      },
    });
  }

  return (
    <Button id="create-shift-trigger-button" className="gap-2" size="sm" onClick={handleOpen}>
      {t(Namespace.Autopark, "cards.planShift")}
    </Button>
  );
};

interface CreateShiftModalFormProps {
  drivers: { id: string; name: string }[];
  trucks: ConnectProto.Truck[];
  activeShifts: { driver_id: number; truck_id: number }[];
  /** In create mode, receives the new shift. */
  onShiftCreated?: (created?: ConnectProto.DriverOnShift) => void;
  truckId?: string;
  activeDriverId?: string;
  activeShiftId?: ShiftId;
  activeShiftStatus?: ConnectProto.DriverOnShiftStatus;
  planStart?: any;
  defaultDate?: Date;
  onClose: () => void;
}

const CreateShiftModalForm: React.FC<CreateShiftModalFormProps> = ({
  drivers,
  trucks,
  activeShifts,
  onShiftCreated,
  truckId,
  activeDriverId,
  activeShiftId,
  activeShiftStatus,
  planStart,
  defaultDate,
  onClose,
}) => {
  const t = useT();
  const isEditMode = !!activeShiftId;

  /** Local 00:00 of today — the earliest day a new shift may be planned for */
  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const initialDate = useMemo(() => {
    const resolved = (() => {
      if (planStart) {
        if (typeof planStart === "object" && ("seconds" in planStart || "nanos" in planStart)) {
          return fromTimestamp(planStart) || defaultDate || new Date();
        }
        return new Date(planStart);
      }
      return defaultDate || new Date();
    })();
    // An unparseable planStart/defaultDate must not leak an Invalid Date into the picker
    if (Number.isNaN(resolved.getTime())) return new Date();
    // A new shift can't start in the past — fall back to today
    if (!isEditMode && resolved.getTime() < startOfToday.getTime()) return new Date();
    return resolved;
  }, [planStart, defaultDate, isEditMode, startOfToday]);

  const [selectedDriverId, setSelectedDriverId] = useState<string>(activeDriverId || "");
  const [selectedTruckId, setSelectedTruckId] = useState<string>(truckId || "");
  const [date, setDate] = useState<Date | undefined>(initialDate);
  /** Whole-hour start of the shift, "HH:00" (24-hour) */
  const initialStartHour = useMemo(() => toHourOption(initialDate), [initialDate]);
  const [startHour, setStartHour] = useState<string>(initialStartHour);

  // Fetch organization data
  const { activeCompanyId } = useAuthSession();
  const selectedCompanyId = activeCompanyId ? String(activeCompanyId) : undefined;

  // Mutations
  const [createDriverOnShift] = useCreateDriverOnShiftMutation();
  const [updateDriverOnShift] = useUpdateDriverOnShiftMutation();
  const [completeDriverOnShift] = useCompleteDriverOnShiftMutation();
  const [changeRouteDriverOnShift] = useChangeRouteDriverOnShiftMutation();
  const [triggerListRoutes] = useLazyListRoutesQuery();

  // Filter out drivers and trucks that already have active shifts
  const availableDrivers = useMemo(() => {
    const driverIdsWithShifts = new Set(
      activeShifts
        .filter((shift) => String(shift.driver_id) !== activeDriverId)
        .map((shift) => shift.driver_id),
    );
    const seen = new Set<string>();
    return drivers.filter((driver) => {
      if (seen.has(driver.id)) return false;
      seen.add(driver.id);
      return !driverIdsWithShifts.has(Number(driver.id));
    });
  }, [drivers, activeShifts, activeDriverId]);

  const availableTrucks = useMemo(() => {
    const truckIdsWithShifts = new Set(
      activeShifts
        .filter((shift) => String(shift.truck_id) !== truckId)
        .map((shift) => shift.truck_id),
    );
    return trucks.filter((truck) => !truckIdsWithShifts.has(Number(truck.id)));
  }, [trucks, activeShifts, truckId]);

  const truckOptions = useMemo(
    () => availableTrucks.map((truck) => ({ id: String(truck.id), name: truck.plateNumber ?? "" })),
    [availableTrucks],
  );

  /** Trailer of the selected truck — read-only here, it is edited on the truck */
  const selectedTruckTrailer = useMemo(() => {
    const truck = trucks.find((t) => t.id.toString() === selectedTruckId);
    return truck?.techPassport ?? "";
  }, [trucks, selectedTruckId]);

  /** Is the selected date today (by local calendar day)? */
  const isTodaySelected = useMemo(() => {
    if (!date) return false;
    const selected = new Date(date);
    if (Number.isNaN(selected.getTime())) return false;
    const now = new Date();
    return (
      selected.getFullYear() === now.getFullYear() &&
      selected.getMonth() === now.getMonth() &&
      selected.getDate() === now.getDate()
    );
  }, [date]);

  /**
   * Hours offerable for the selected day. A new shift may not start in the past,
   * so today drops the hours already gone; an existing shift keeps all 24 —
   * its start may legitimately sit in the past.
   */
  const availableHours = useMemo(() => {
    if (isEditMode || !isTodaySelected) return HOUR_OPTIONS;
    return HOUR_OPTIONS.slice(new Date().getHours());
  }, [isEditMode, isTodaySelected]);

  // Moving the date onto today can strand the picked hour in the past — snap it forward
  useEffect(() => {
    if (!availableHours.includes(startHour)) setStartHour(availableHours[0]);
  }, [availableHours, startHour]);

  /** Selected day at the selected whole hour — the shift's `tw_start_at` */
  const startAt = useMemo(() => {
    if (!date) return undefined;
    const start = new Date(date);
    // Guard against an Invalid Date producing a nonsense Timestamp
    if (Number.isNaN(start.getTime())) return undefined;
    const hour = Number.parseInt(startHour, 10);
    start.setHours(Number.isFinite(hour) ? hour : 0, 0, 0, 0);
    return start;
  }, [date, startHour]);

  /** `tw_end_at` on edit: 12 hours after the chosen start */
  const endAt = useMemo(() => {
    if (!startAt) return undefined;
    const end = new Date(startAt);
    end.setHours(end.getHours() + SHIFT_DURATION_HOURS);
    return end;
  }, [startAt]);

  /** Has the start hour been moved away from the shift's current one? */
  const isStartHourChanged = startHour !== initialStartHour;

  // One flag for the whole submit: the edit path chains create -> list routes ->
  // reassign -> complete, and a per-mutation `isLoading` goes false between them.
  const [isSaving, setIsSaving] = useState(false);

  // Handler: create or update a shift
  const handleCreateShift = async () => {
    if (!selectedTruckId || !selectedCompanyId || !date || !startAt || !endAt) {
      console.error("Не выбраны машина, дата, время или организация");
      return;
    }
    setIsSaving(true);

    try {
      // Inside the try: `BigInt` throws on a non-numeric id, and escaping the
      // `finally` would leave the button disabled for the life of the modal.
      const orgId = BigInt(selectedCompanyId);
      const dateTs = toTimestamp(startAt);
      const endTs = toTimestamp(endAt);
      const driverId = selectedDriverId ? BigInt(selectedDriverId) : undefined;
      let created: ConnectProto.DriverOnShift | undefined;

      if (isEditMode && selectedDriverId !== activeDriverId) {
        // --- EDIT MODE: driver changed ---
        const isNotApproved =
          activeShiftStatus === ConnectProto.DriverOnShiftStatus.NOT_APPROVED ||
          activeShiftStatus === ConnectProto.DriverOnShiftStatus.CONFIRMATION;

        if (isNotApproved) {
          // Just update driver_id on the existing shift
          await updateDriverOnShift({
            id: BigInt(activeShiftId),
            ownerOrganizationId: orgId,
            driverId,
            twStartAt: dateTs,
            twEndAt: endTs,
          }).unwrap();
        } else {
          // 1. Create a new shift with same truck but new driver
          const newShift = await createDriverOnShift({
            ownerOrganizationId: orgId,
            driverId,
            truckId: BigInt(selectedTruckId),
            twStartAt: dateTs,
            twEndAt: endTs,
            status: ConnectProto.DriverOnShiftStatus.NOT_APPROVED,
          }).unwrap();

          // 2. Reassign all routes from old shift to new shift
          try {
            const routesResult = await triggerListRoutes({
              ownerOrganizationIds: [orgId],
              driverOnShiftIds: [BigInt(activeShiftId)],
            }).unwrap();

            const routes = routesResult?.routes || [];
            for (const route of routes) {
              // Skip completed/cancelled routes
              if (
                route.status === ConnectProto.RouteStatus.COMPLETED ||
                route.status === ConnectProto.RouteStatus.CANCELLED ||
                route.status === ConnectProto.RouteStatus.FORCE_COMPLETED
              ) {
                continue;
              }
              await changeRouteDriverOnShift({
                routeId: route.id,
                driverOnShiftId: newShift.id,
                ownerOrganizationId: orgId,
              }).unwrap();
            }
          } catch (routeError) {
            console.error("Ошибка при переназначении маршрутов:", routeError);
            throw routeError;
          }

          // 3. Force complete the old shift
          await completeDriverOnShift({
            ownerOrganizationId: orgId,
            id: BigInt(activeShiftId),
          }).unwrap();
        }
      } else if (!isEditMode) {
        // --- CREATE MODE ---
        created = await createDriverOnShift({
          ownerOrganizationId: orgId,
          driverId,
          truckId: BigInt(selectedTruckId),
          twStartAt: dateTs,
          status: ConnectProto.DriverOnShiftStatus.NOT_APPROVED,
        }).unwrap();
      } else if (isStartHourChanged) {
        // --- EDIT MODE: driver unchanged, only the start hour moved ---
        await updateDriverOnShift({
          id: BigInt(activeShiftId),
          ownerOrganizationId: orgId,
          twStartAt: dateTs,
          twEndAt: endTs,
        }).unwrap();
      } else {
        // isEditMode && nothing changed — nothing to persist, just close
        onClose();
        return;
      }

      onClose();
      onShiftCreated?.(created);
    } catch (error) {
      console.error("Ошибка при создании/обновлении смены:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full">
      <div className="flex gap-4 p-4 flex-1 w-full">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-1 w-full">
            <Label htmlFor="truck" className="text-right flex flex-1 items-center">
              {t(Namespace.Autopark, "modals.createShift.truck.label")}
            </Label>
            <div className="flex flex-1 justify-end items-center">
              <SearchableSelect
                id="create-shift-truck-select"
                options={truckOptions}
                match="compact"
                value={selectedTruckId}
                onChange={setSelectedTruckId}
                placeholder={t(Namespace.Autopark, "modals.createShift.truck.placeholder")}
                emptyLabel={t(Namespace.Autopark, "cards.noAvailableTrucks")}
                searchPlaceholder={t(Namespace.Autopark, "modals.createShift.search.placeholder")}
                disabled={isEditMode || !!truckId}
              />
            </div>
          </div>
          <div className="flex flex-1 w-full justify-between">
            <Label htmlFor="trailer" className="text-right items-center flex flex-1">
              {t(Namespace.Autopark, "modals.truckModal.techPassport.label")}
            </Label>
            <div className="flex flex-1 justify-end items-center">
              <Input
                id="trailer"
                value={selectedTruckTrailer}
                disabled
                placeholder="—"
                className="w-full h-9"
              />
            </div>
          </div>
          <div className="flex flex-1 w-full justify-between">
            <Label htmlFor="date" className="text-right items-center flex flex-1">
              {t(Namespace.Autopark, "modals.createShift.date.label")}
            </Label>
            <div className="flex flex-1 justify-end items-center">
              <DatePicker
                id="date"
                value={date}
                className="!w-full"
                onChange={setDate}
                placeholder={t(Namespace.Autopark, "modals.createShift.date.placeholder")}
                disabled={isEditMode}
                minDate={isEditMode ? undefined : startOfToday}
              />
            </div>
          </div>
          <div className="flex flex-1 w-full justify-between">
            <Label htmlFor="start-hour" className="text-right items-center flex flex-1">
              {t(Namespace.Autopark, "modals.createShift.time.label")}
            </Label>
            <div className="flex flex-1 justify-end items-center">
              <Select value={startHour} onValueChange={setStartHour}>
                <SelectTrigger id="start-hour" className="w-full h-9 font-normal">
                  <SelectValue
                    placeholder={t(Namespace.Autopark, "modals.createShift.time.placeholder")}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableHours.map((hour) => (
                    <SelectItem key={hour} value={hour}>
                      {hour}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-1 w-full justify-between">
            <Label htmlFor="driver" className="text-right flex flex-1 items-center">
              {t(Namespace.Autopark, "modals.createShift.driver.label")}
            </Label>
            <div className="flex flex-1 justify-end items-center">
              <SearchableSelect
                id="create-shift-driver-select"
                options={availableDrivers}
                value={selectedDriverId}
                onChange={setSelectedDriverId}
                clearOption={{
                  id: "",
                  name: t(Namespace.Autopark, "modals.createShift.driver.unassigned"),
                }}
                emptyLabel={t(Namespace.Autopark, "cards.noAvailableDrivers")}
                searchPlaceholder={t(Namespace.Autopark, "modals.createShift.search.placeholder")}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex border-t justify-end gap-2 p-4">
        <Button
          id="create-shift-submit-button"
          onClick={handleCreateShift}
          disabled={
            !selectedTruckId ||
            !date ||
            isSaving ||
            (!truckId && availableTrucks.length === 0) ||
            (isEditMode && selectedDriverId === activeDriverId && !isStartHourChanged)
          }
        >
          {isSaving
            ? "Сохранение..."
            : activeShiftId
              ? t(Namespace.Common, "save") || "Сохранить"
              : t(Namespace.Autopark, "modals.createShift.buttons.create")}
        </Button>
      </div>
    </div>
  );
};
